import React, { useState, useEffect } from 'react';
import { TextInput, ReferenceInput, AutocompleteInput, Form, ListButton, CreateButton, useTranslate, useGetResourceLabel, useCreate } from 'react-admin';
import { Box, Card } from '@mui/material';
import moment from 'moment';

import Breadcrumb from '../../common/layout/Breadcrumb';
import HelpButton from '../buttons/HelpButton';
import ProjectInput from '../../projects/inputs/ProjectInput';
import Timer from './Timer';
import LastTimeEntries from './LastTimeEntries';

import time_entries from '../index';


const STORAGE_KEY = 'timesheet-timer-state';

const optionText = choice => choice?.name ? choice.name : '';

const formSx = {
    display: 'flex',
    gap: '5px',
    marginBottom: '15px',
    flexWrap: 'wrap',
    width: '100%',
    '& .MuiFormControl-root, && .MuiAutocomplete-root': {
        minWidth: '200px !important',
        height: '100%',
        maxHeight: '65px',
        '.MuiInputBase-root': {
            height: '100%',
        },
        '&.ra-input-description': {
            flex: 1
        },
        '&.ra-input-project_id': {
            flex: 1,
            '& .MuiFormControl-root': {
                minWidth: '100% !important'
            }
        },
        '&.ra-input-notes': {
            flex: 1
        },
        '@media (max-width: 701px)': {
            height: 'auto',
            maxHeight: 'none',
            width: '100%',
            maxWidth: 'none'
        }
    }
};

const TimeEntryTimerCard = () => {
    const [timerReady, setTimerReady] = useState(false);
    const [offsetTimer, setOffsetTimer] = useState();
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [startAt, setStartAt] = useState();
    const [lastPauseAt, setLastPauseAt] = useState();
    const [pauseMs, setPauseMs] = useState(0);

    const [description, setDescription] = useState();
    const [projectId, setProjectId] = useState();
    const [typeId, setTypeId] = useState();
    const [notes, setNotes] = useState();

    const [createTimeEntry] = useCreate('time_entries');
    const translate = useTranslate();
    const status = isPaused ? 'paused' : isTimerRunning ? 'running' : 'idle';

    const loadTimerState = () => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                return JSON.parse(raw);
            }
        } catch {

        }
        return {
            status: 'idle',
            project_id: null,
            type_id: null,
            description: '',
            notes: '',
            start_at: null,
            last_pause_at: null,
            pause_ms: 0,
        };
    };

    const saveState = (state) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {

        }
    };

    const clearState = () => {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {

        }
    };

    useEffect(() => {
        const timer = loadTimerState();

        setProjectId(timer.project_id ?? null);
        setTypeId(timer.type_id ?? null);
        setDescription(timer.description ?? '');
        setNotes(timer.notes ?? '');

        if (['running', 'paused'].includes(timer.status) && timer.start_at) {
            const now = Date.now();
            const basePause = timer.pause_ms || 0;
            const elapsedMs = timer.status === 'paused' && timer.last_pause_at
                ? (timer.last_pause_at - timer.start_at) - basePause
                : (now - timer.start_at) - basePause;
            setOffsetTimer(new Date(now + Math.max(elapsedMs, 0)));

            if (timer.status === 'paused' && timer.last_pause_at) {
                setIsPaused(true);
                setStartAt(timer.start_at);
                setLastPauseAt(timer.last_pause_at);
                setPauseMs(basePause);
            } else {
                setIsTimerRunning(true);
                setStartAt(timer.start_at);
                setLastPauseAt(timer.last_pause_at ?? null);
                setPauseMs(basePause);
            }
        }
        setTimerReady(true);
    }, []);

    useEffect(() => {
        if (!timerReady) {
            return;
        }

        const isIdle = status === 'idle';
        const state = {
            status,
            project_id: projectId,
            type_id: typeId,
            description,
            notes,
            start_at: isIdle ? null : startAt,
            pause_ms: isIdle ? 0 : pauseMs,
            last_pause_at: isIdle ? null : lastPauseAt,
        };
        saveState(state);
    }, [timerReady, status, projectId, typeId, description, notes, startAt, pauseMs, lastPauseAt]);

    // TODO: Uncomment when backend done
    // useEffect(() => {
    //     const fetchLastTimeEntry = async () => {
    //         const response = await fetchLastTimeEntry();
    //         if (['in_progress', 'paused'].includes(response?.status)) {
    //             const now = DateTime.now().toUTC();
    //             const checkInDate = DateTime.fromISO(response.check_in_at, { zone: 'utc' });
    //             const diff = now.ts - checkInDate.ts;
    //             let offset = now.ts + diff - response.pause_duration * 1000;

    //             if (response.is_paused) {
    //                 const lastPausedDate = DateTime.fromISO(response.last_paused_at, { zone: 'utc' });
    //                 const pauseDuration = (now.ts - lastPausedDate.ts);
    //                 setIsPaused(response.is_paused);
    //                 offset -= pauseDuration;
    //             }
    //             else {
    //                 setIsTimerRunning(true);
    //             }
    //             setOffsetTimer(offset);
    //         }
    //         setTimerReady(true);
    //     };

    //     fetchLastTimeEntry();
    // }, []);

    const handleStart = async () => {
        const now = Date.now();
        setStartAt(now);
        setPauseMs(0);
        setLastPauseAt(null);
        setOffsetTimer(now);
        setIsPaused(false);
        setIsTimerRunning(true);

        // TODO: add api call to start time entry timer
        // await startTimeEntryTimer();
    };

    const handleStop = async (hours) => {
        // TODO: add api call to stop time entry timer
        // await stopTimeEntryTimer();
        setOffsetTimer(null);
        setStartAt(null);
        setPauseMs(0);
        setLastPauseAt(null);
        setIsPaused(false);
        setIsTimerRunning(false);
        clearState();

        // TODO: remove when backend done
        const data = {
            date: moment().format('YYYY-MM-DD'),
            hours,
            description,
            project_id: projectId,
            type_id: typeId,
            notes
        };
        createTimeEntry('time_entries', { data });

        setDescription('');
        setProjectId(null);
        setTypeId(null);
        setNotes('');
    };

    const handlePauseToggle = () => {
        if (isPaused) {
            if (lastPauseAt) {
                setPauseMs(prev => prev + (Date.now() - lastPauseAt));
                setLastPauseAt(null);
            }
            setIsPaused(false);
            setIsTimerRunning(true);

            // TODO: add api call to resume
            // await resumeTimeEntry();
        } else {
            // Pause: remember when the pause started.
            setLastPauseAt(Date.now());
            setIsPaused(true);
            setIsTimerRunning(false);

            // TODO: add api call to pause
            // await pauseTimeEntry();
        }
    };

    const handleReset = () => {
        setDescription('');
        setProjectId(null);
        setTypeId(null);
        setNotes('');
    };

    const handleChangeDescription = (e) => {
        setDescription(e.target.value);
    };

    const handleChangeProject = (id) => {
        setProjectId(id || null);
    };

    const handleChangeType = (id) => {
        setTypeId(id || null);
    };

    const handleChangeNotes = (e) => {
        setNotes(e.target.value);
    };

    if (!timerReady) {
        return null;
    }

    return (
        <Card sx={{ padding: '30px' }}>
            <Form defaultValues={{ description, project_id: projectId, type_id: typeId, notes }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <TextInput label={translate('What are you working on?')} source="description" multiline helperText={false} onChange={handleChangeDescription} sx={{ marginBottom: '15px' }} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                            <Box sx={formSx}>
                                <ProjectInput helperText={false} onChange={handleChangeProject} />
                                <ReferenceInput source="type_id" reference="time_entry_types" sort={{ field: 'name', order: 'ASC' }}>
                                    <AutocompleteInput optionText={optionText} helperText={false} onChange={handleChangeType} />
                                </ReferenceInput>
                                <TextInput source="notes" multiline helperText={false} onChange={handleChangeNotes} />
                            </Box>
                        </Box>
                    </Box>
                    {timerReady && (
                        <Timer
                            onStart={handleStart}
                            onStop={handleStop}
                            onPause={handlePauseToggle}
                            onReset={handleReset}
                            isRunning={isTimerRunning}
                            setIsRunning={setIsTimerRunning}
                            isPaused={isPaused}
                            setIsPaused={setIsPaused}
                            offsetTimestamp={offsetTimer}
                            description={description}
                            projectId={projectId}
                            typeId={typeId}
                            notes={notes}
                        />
                    )}
                </Box>
            </Form>
        </Card>
    );
};

const TimeEntryTimer = () => {
    const translate = useTranslate();
    const getResourceLabel = useGetResourceLabel();

    const breadcrumbPath = [
        { url: '/time_entries', title: translate('resources.time_entries.name', { smart_count: 2, _: getResourceLabel('time_entries', 2) }), isActive: false, icon: <time_entries.icon /> },
        { url: 'time_entries/timer', title: translate('Timer'), isActive: true }
    ];

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Breadcrumb path={breadcrumbPath} />
                <Box>
                    <HelpButton />
                    <CreateButton resource="time_entries" />
                    <ListButton resource="time_entries" />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', maxHeight: '651px', overflow: 'hidden', marginBottom: '10px', paddingRight: 2 }}>
                <TimeEntryTimerCard />
                <LastTimeEntries />
            </Box>
        </>
    );
};

export default TimeEntryTimer;
export { TimeEntryTimerCard };